import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  rafflesRef,
  entriesRef,
  handoversRef,
  query,
  where,
  orderBy,
  limit,
  executeQuery,
  subscribeToQuery,
} from '../firebase/firestore';

/**
 * useDashboard
 *
 * Aggregated hook for dashboard data:
 * - User's entries (active, won, lost)
 * - User's created raffles (if seller)
 * - Stats (total entered, won, spent, earned)
 */
export function useDashboard() {
  const { user, userProfile } = useAuth();
  const [entries, setEntries] = useState([]);
  const [sellerRaffles, setSellerRaffles] = useState([]);
  const [pendingHandovers, setPendingHandovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user's entries
  useEffect(() => {
    if (!user?.uid) {
      setEntries([]);
      setLoading(false);
      return;
    }

    const q = query(
      entriesRef,
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = subscribeToQuery(q, (results) => {
      setEntries(results);
      setLoading(false);
    }, (err) => {
      console.error('Error fetching entries:', err);
      setError(err);
      setLoading(false);
    });

    return unsubscribe;
  }, [user?.uid]);

  // Fetch seller's raffles (if they've created any)
  useEffect(() => {
    if (!user?.uid) {
      setSellerRaffles([]);
      return;
    }

    const q = query(
      rafflesRef,
      where('sellerId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const unsubscribe = subscribeToQuery(q, (results) => {
      setSellerRaffles(results);
    }, (err) => {
      console.error('Error fetching seller raffles:', err);
    });

    return unsubscribe;
  }, [user?.uid]);

  // Fetch pending handovers (where user is winner or seller)
  useEffect(() => {
    if (!user?.uid) {
      setPendingHandovers([]);
      return;
    }

    // Note: Firestore doesn't support OR queries easily, so we fetch both separately
    const fetchHandovers = async () => {
      try {
        // Handovers where user is winner
        const winnerQuery = query(
          handoversRef,
          where('winnerId', '==', user.uid),
          where('status', '==', 'pending')
        );
        const winnerHandovers = await executeQuery(winnerQuery);

        // Handovers where user is seller
        const sellerQuery = query(
          handoversRef,
          where('sellerId', '==', user.uid),
          where('status', '==', 'pending')
        );
        const sellerHandovers = await executeQuery(sellerQuery);

        // Merge and dedupe (in case of overlap, though unlikely)
        const all = [...winnerHandovers, ...sellerHandovers];
        const unique = Array.from(new Map(all.map(h => [h.id, h])).values());
        setPendingHandovers(unique);
      } catch (err) {
        console.error('Error fetching handovers:', err);
      }
    };

    fetchHandovers();
  }, [user?.uid]);

  // Compute stats
  const stats = useMemo(() => {
    const activeEntries = entries.filter(e => e.raffleStatus === 'active');
    const wonEntries = entries.filter(e => e.isWinner === true);
    const lostEntries = entries.filter(e => e.raffleStatus === 'completed' && !e.isWinner);
    const refundedEntries = entries.filter(e => e.refundStatus === 'completed');

    const totalSpent = entries.reduce((sum, e) => sum + (e.totalPaid || 0), 0);
    const activeRaffles = sellerRaffles.filter(r => r.status === 'active');
    const completedRaffles = sellerRaffles.filter(r => r.status === 'completed');

    // Calculate earnings from completed raffles
    const totalEarnings = completedRaffles.reduce((sum, r) => {
      return sum + (r.ticketsSold || 0) * (r.ticketPrice || 0);
    }, 0);

    return {
      // Buyer stats
      totalEntered: entries.length,
      activeEntries: activeEntries.length,
      wins: wonEntries.length,
      losses: lostEntries.length,
      refunded: refundedEntries.length,
      totalSpent,

      // Seller stats
      totalCreated: sellerRaffles.length,
      activeRaffles: activeRaffles.length,
      completedRaffles: completedRaffles.length,
      totalEarnings,
      pendingHandovers: pendingHandovers.length,
    };
  }, [entries, sellerRaffles, pendingHandovers]);

  // Categorized entries
  const categorizedEntries = useMemo(() => {
    return {
      active: entries.filter(e => e.raffleStatus === 'active'),
      won: entries.filter(e => e.isWinner === true),
      lost: entries.filter(e => e.raffleStatus === 'completed' && !e.isWinner),
      refunded: entries.filter(e => e.refundStatus === 'completed'),
    };
  }, [entries]);

  // Categorized seller raffles
  const categorizedRaffles = useMemo(() => {
    return {
      active: sellerRaffles.filter(r => r.status === 'active'),
      completed: sellerRaffles.filter(r => r.status === 'completed'),
      cancelled: sellerRaffles.filter(r => r.status === 'cancelled'),
      pending: sellerRaffles.filter(r => r.status === 'pending'),
    };
  }, [sellerRaffles]);

  return {
    // Raw data
    entries,
    sellerRaffles,
    pendingHandovers,

    // Categorized
    categorizedEntries,
    categorizedRaffles,

    // Stats
    stats,

    // Status
    loading,
    error,

    // Flags
    isSeller: sellerRaffles.length > 0 || userProfile?.stripeConnectStatus === 'active',
    hasActiveRaffle: sellerRaffles.some(r => r.status === 'active'),
  };
}

/**
 * useUserEntries
 *
 * Hook for fetching user's entries with raffle data joined.
 * Used for My Entries page with filtering.
 */
export function useUserEntries(options = {}) {
  const { status = 'all', limit: limitCount = 50 } = options;
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [raffles, setRaffles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch entries
  useEffect(() => {
    if (!user?.uid) {
      setEntries([]);
      setLoading(false);
      return;
    }

    const constraints = [
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(limitCount),
    ];

    const q = query(entriesRef, ...constraints);

    const unsubscribe = subscribeToQuery(q, async (results) => {
      setEntries(results);

      // Fetch associated raffles
      const raffleIds = [...new Set(results.map(e => e.raffleId))];
      if (raffleIds.length > 0) {
        try {
          const rafflePromises = raffleIds.map(async (id) => {
            const raffleQuery = query(rafflesRef, where('__name__', '==', id));
            const raffleResults = await executeQuery(raffleQuery);
            return raffleResults[0] || null;
          });

          // Firestore doesn't support __name__ in array, so fetch individually
          const raffleMap = {};
          for (const id of raffleIds) {
            const { getDocument } = await import('../firebase/firestore');
            const raffle = await getDocument('raffles', id);
            if (raffle) {
              raffleMap[id] = raffle;
            }
          }
          setRaffles(raffleMap);
        } catch (err) {
          console.error('Error fetching raffles for entries:', err);
        }
      }

      setLoading(false);
    }, (err) => {
      console.error('Error fetching user entries:', err);
      setError(err);
      setLoading(false);
    });

    return unsubscribe;
  }, [user?.uid, limitCount]);

  // Filter entries based on status
  const filteredEntries = useMemo(() => {
    if (status === 'all') return entries;

    return entries.filter((entry) => {
      switch (status) {
        case 'active':
          return entry.raffleStatus === 'active';
        case 'won':
          return entry.isWinner === true;
        case 'lost':
          return entry.raffleStatus === 'completed' && !entry.isWinner;
        case 'refunded':
          return entry.refundStatus === 'completed' || entry.raffleStatus === 'cancelled';
        default:
          return true;
      }
    });
  }, [entries, status]);

  // Join entries with raffle data
  const entriesWithRaffles = useMemo(() => {
    return filteredEntries.map((entry) => ({
      ...entry,
      raffle: raffles[entry.raffleId] || null,
    }));
  }, [filteredEntries, raffles]);

  return {
    entries: entriesWithRaffles,
    loading,
    error,
    totalCount: entries.length,
    filteredCount: filteredEntries.length,
  };
}

/**
 * useSellerRaffles
 *
 * Hook for fetching user's created raffles.
 * Used for My Competitions page.
 */
export function useSellerRaffles(options = {}) {
  const { status = 'all', limit: limitCount = 50 } = options;
  const { user } = useAuth();
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.uid) {
      setRaffles([]);
      setLoading(false);
      return;
    }

    const constraints = [
      where('sellerId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(limitCount),
    ];

    const q = query(rafflesRef, ...constraints);

    const unsubscribe = subscribeToQuery(q, (results) => {
      setRaffles(results);
      setLoading(false);
    }, (err) => {
      console.error('Error fetching seller raffles:', err);
      setError(err);
      setLoading(false);
    });

    return unsubscribe;
  }, [user?.uid, limitCount]);

  // Filter by status
  const filteredRaffles = useMemo(() => {
    if (status === 'all') return raffles;

    return raffles.filter((raffle) => raffle.status === status);
  }, [raffles, status]);

  // Compute stats
  const stats = useMemo(() => {
    const active = raffles.filter(r => r.status === 'active');
    const completed = raffles.filter(r => r.status === 'completed');
    const cancelled = raffles.filter(r => r.status === 'cancelled');

    const totalEarnings = completed.reduce((sum, r) => {
      return sum + (r.ticketsSold || 0) * (r.ticketPrice || 0);
    }, 0);

    const totalEntriesSold = raffles.reduce((sum, r) => sum + (r.ticketsSold || 0), 0);

    return {
      total: raffles.length,
      active: active.length,
      completed: completed.length,
      cancelled: cancelled.length,
      totalEarnings,
      totalEntriesSold,
      completionRate: raffles.length > 0
        ? Math.round((completed.length / (completed.length + cancelled.length || 1)) * 100)
        : 0,
    };
  }, [raffles]);

  return {
    raffles: filteredRaffles,
    allRaffles: raffles,
    stats,
    loading,
    error,
  };
}
